import { Component, DestroyRef, Input, OnInit } from '@angular/core';
import { ModalNavigationComponent } from '../../../../services/gui/modal/modal-navigation-component';
import {
  API,
  GroupMediumDTO,
  InventoryMarkerFullDTO,
  MarkerDTO,
  MarkerFullDTO,
  MediumDTO,
  MediumGroupAttributeDTO,
  MediumGroupCategoryDTO,
  MediumGroupFullDTO,
  ProductDTO,
  ResolutionDTO,
} from '../../../../services/api/api';
import { ActivatedRoute } from '@angular/router';
import { ModalService } from '../../../../services/gui/modal/modal.service';
import { ModalNavigationService } from '../../../../services/gui/modal/modal-navigation.service';
import { ErrorService } from '../../../../services/error.service';
import { TranslateService } from '@ngx-translate/core';
import { UtilService } from '../../../../services/util.service';
import { NavigationService } from '@ngx-seatback/utils';
import { MediumGroupsComponent } from '../medium-groups.component';
import { ProductsComponent } from 'src/app/pages/datasets/products/products.component';
import { PermissionService } from '../../../../services/permission.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize, take } from 'rxjs/operators';
import { MediumGroupCategoryEditComponent } from '../mediumGroup-category-edit/medium-group-category-edit.component';
import { MediumGroupCategoryListComponent } from '../mediumGroup-category-list/medium-group-category-list.component';

@Component({
  selector: 'sb-page-medium-group-edit',
  templateUrl: './medium-group-edit.component.html',
  styleUrls: ['./medium-group-edit.component.scss'],
  standalone: false,
})
export class MediumGroupEditComponent implements OnInit, ModalNavigationComponent {
  @Input() modal: any;
  loading: boolean;
  mediumGroupId: number;
  mediumGroup: MediumGroupFullDTO;
  possibleMarkers: MarkerFullDTO[];
  selectedMarkers: MarkerFullDTO[];
  isInModalContext: boolean;
  debounceTimer: ReturnType<typeof setTimeout>;
  missingResolution: ResolutionDTO[];
  selectedResolution: ResolutionDTO;
  setOfMarkerResolutions: ResolutionDTO[];
  duplicateResolutions: ResolutionDTO[];
  inventoryMarker: InventoryMarkerFullDTO[];
  missingMarker: InventoryMarkerFullDTO[];
  _medium: MediumDTO;
  attributesEdit: boolean;
  mostOccurredDuration: number;
  showTimeDifferenceWarning: boolean;
  hasDuplicateResolution: boolean;

  constructor(
    private api: API,
    private activatedRoute: ActivatedRoute,
    private modalService: ModalService,
    private modalNavigation: ModalNavigationService,
    public errorHandler: ErrorService,
    public translate: TranslateService,
    public util: UtilService,
    private navService: NavigationService,
    public permissionService: PermissionService,
    private destroyRef: DestroyRef
  ) {
    this.hasDuplicateResolution = false;
    this.mostOccurredDuration = 0;
    this.showTimeDifferenceWarning = false;
    this.mediumGroupId = undefined;
    this.mediumGroup = undefined;
    this.possibleMarkers = [];
    this.selectedMarkers = [];
    this.isInModalContext = false;
    this.debounceTimer = null;
    this.selectedResolution = undefined;
    this.missingResolution = [];
    this.setOfMarkerResolutions = [];
    this.duplicateResolutions = [];
    this.missingMarker = [];
    this.inventoryMarker = [];
    this.loading = true;
  }

  get medium(): MediumDTO {
    return this._medium;
  }

  set medium(val: MediumDTO) {
    this._medium = val;
  }

  ngOnInit() {
    if (this.isInModalContext) {
      this.mediumGroupId = this.modalNavigation.getCurrentNavigation().data.id;
    } else {
      this.activatedRoute.params.subscribe(params => {
        this.mediumGroupId = params.mediumGroupID;
      });
    }
    this.refreshFromServer();
  }

  refreshFromServer() {
    this.loading = true;
    this.api
      .mm_MediumGroup_retrieveById(this.mediumGroupId)
      .pipe(takeUntilDestroyed(this.destroyRef), take(1))
      .subscribe({
        next: (receivedMediumGroup: MediumGroupFullDTO) => {
          this.mediumGroup = receivedMediumGroup;
          this.api
            .ds_rm_Inventory_Markers()
            .pipe(
              takeUntilDestroyed(this.destroyRef),
              take(1),
              finalize(() => {
                this.loading = false;
              })
            )
            .subscribe({
              next: (markerData: InventoryMarkerFullDTO[]) => {
                this.inventoryMarker = markerData;
                this.createResolutionArrays(markerData).then(() => {
                  this.checkWarningConditions();
                });
              },
              error: error => {
                this.errorHandler.errorHandler(error);
              },
            });
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
  }

  editMediumGroup() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    this.debounceTimer = setTimeout(() => {
      this.api.mm_MediumGroup_edit(this.mediumGroup).subscribe({
        next: (response: MediumGroupFullDTO) => {
          this.mediumGroup = response;
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
    }, 2000);
  }

  isPreviewClass(medium: GroupMediumDTO): string {
    return medium.isPreview ? 'yellow' : '';
  }

  setToPreview(medium: GroupMediumDTO): void {
    this.mediumGroup.preview = medium;
    this.editMediumGroup();
  }

  addNewMotiveToGroup(resolution?: ResolutionDTO) {
    if (resolution) {
      this.selectedResolution = resolution;
    }
    this.modalService
      .modal(
        {
          title: this.translate.instant('lang.motiveGroups.addMotiveToGroup'),
          modalOptions: { windowClass: 'bigXL' },
        },
        {
          templateId: 'mediumExplorerModal',
        },
        { startSearch: true } // <--- payload
      )
      .then((result: MediumDTO[]) => {
        this.api
          .mm_MediumGroup_addMedia(this.mediumGroup.id, result)
          .pipe(takeUntilDestroyed(this.destroyRef), take(1))
          .subscribe({
            next: (mediumGroup: MediumGroupFullDTO) => {
              this.mediumGroup = mediumGroup;
              this.selectedResolution = undefined;
              this.checkWarningConditions();
            },
            error: error => {
              this.errorHandler.errorHandler(error);
            },
          });
      })
      .catch(() => {
        this.selectedResolution = undefined;
      });
  }

  removeMotiveFromGroup(medium: MediumDTO) {
    this.modalService
      .confirm({
        title: this.translate.instant('lang.motiveGroups.removeMedium', { medium: medium.name }),
        message: this.translate.instant('lang.motiveGroups.removeMediumFromGroup', {
          medium: medium.name,
          group: this.mediumGroup.name,
        }),
        yes: this.translate.instant('lang.globals.yes'),
        no: this.translate.instant('lang.globals.no'),
      })
      .then((result: boolean) => {
        if (result) {
          this.api
            .mm_MediumGroup_removeMedium(this.mediumGroup.id, medium.id)
            .pipe(takeUntilDestroyed(this.destroyRef), take(1))
            .subscribe({
              next: data => {
                this.mediumGroup = data;
                this.checkWarningConditions();
              },
              error: error => {
                this.errorHandler.errorHandler(error);
              },
            });
        }
      })
      .catch(() => {});
  }

  private checkWarningConditions() {
    // length difference
    if (this.mediumGroup.media && Array.isArray(this.mediumGroup.media)) {
      const videoMedia = this.mediumGroup.media
        .map(medium => {
          if (medium.contentType.startsWith('video')) {
            return medium;
          }
        })
        .filter(Boolean);
      this.mostOccurredDuration = this.getMostOccurredDuration(videoMedia);
      this.showTimeDifferenceWarning = videoMedia.some(
        medium => medium.duration >= this.mostOccurredDuration + 0.5 || medium.duration <= this.mostOccurredDuration - 0.5
      );
    }
    // missing resolutions
    this.updateMissingResolutions();
    // duplicate resolutions
    this.updateDuplicateResolutions();
  }

  getMostOccurredDuration(media: GroupMediumDTO[]) {
    if (!media || media.length === 0) {
      return 0;
    }
    const durations = media.map(medium => Math.round(medium.duration));
    const countMap = {};
    let maxElement = durations[0];
    let maxCount = 1;
    for (const duration of durations) {
      if (countMap[duration] == null) {
        countMap[duration] = 1;
      } else {
        countMap[duration]++;
      }
      if (countMap[duration] > maxCount) {
        maxElement = duration;
        maxCount = countMap[duration];
      }
    }
    return maxElement;
  }

  deleteMediumGroup(mediumGroup: MediumGroupFullDTO) {
    this.modalService
      .confirm({
        title: this.translate.instant('lang.globals.deleteElementValue', { value: mediumGroup.name }),
        message: this.translate.instant('lang.globals.deleteElementValueQuestion', { value: mediumGroup.name }),
        yes: this.translate.instant('lang.globals.yes'),
        no: this.translate.instant('lang.globals.no'),
      })
      .then((result: boolean) => {
        if (result) {
          this.api
            .mm_MediumGroup_deleteMediumGroup(mediumGroup.id)
            .pipe(takeUntilDestroyed(this.destroyRef), take(1))
            .subscribe({
              next: () => {
                this.navService.navigateToPoint(this.util.RoutingID.mediumGroup);
              },
              error: error => {
                this.errorHandler.errorHandler(error);
              },
            });
        }
      })
      .catch(() => {});
  }

  addAdSpace(marker: MarkerDTO[]) {
    const selectedMedium = this.medium;
    this.api
      .mm_MediumGroup_addDefaultMarkers(this.mediumGroup.id, selectedMedium.id, marker)
      .pipe(takeUntilDestroyed(this.destroyRef), take(1))
      .subscribe({
        next: data => {
          this.mediumGroup = data;
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
  }

  removeAdSpace(medium: MediumDTO, marker: MarkerDTO) {
    this.modalService
      .confirm({
        title: this.translate.instant('lang.motiveGroups.removeMarkerDefaultAdSpace', { marker: marker.name }),
        message: this.translate.instant('lang.motiveGroups.removeMarkerFromMediumDefaultAdSpace', {
          marker: marker.name,
          medium: medium.name,
        }),
        yes: this.translate.instant('lang.globals.yes'),
        no: this.translate.instant('lang.globals.no'),
      })
      .then((result: boolean) => {
        if (result) {
          this.api
            .mm_MediumGroup_removeDefaultMarker(this.mediumGroup.id, medium.id, marker.id)
            .pipe(takeUntilDestroyed(this.destroyRef), take(1))
            .subscribe({
              next: data => {
                this.mediumGroup = data;
              },
              error: error => {
                this.errorHandler.errorHandler(error);
              },
            });
        }
      })
      .catch(() => {});
  }

  selectMediumGroup(mediumGroup: MediumGroupFullDTO) {
    this.modalNavigation.result(mediumGroup);
  }

  showGroupList() {
    this.modalNavigation.navigate(MediumGroupsComponent, null);
  }

  addAdSpaceClose(marker: MarkerFullDTO[]) {
    this.modalService.close(`mediagroupaddmarkermodal`, marker);
  }

  showMarkerToAdd(medium: MediumDTO) {
    this.medium = medium;
    this.possibleMarkers = null;
    this.selectedMarkers = [];

    this.api
      .mm_MediumGroup_retrievePossibleMarkersInMediumGroupByMediumId(this.mediumGroupId, medium.id)
      .pipe(takeUntilDestroyed(this.destroyRef), take(1))
      .subscribe({
        next: data => {
          this.possibleMarkers = data;
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
    this.modalService
      .modal(
        {
          title: this.translate.instant('lang.mediumGroupEdit.addAdspace'),
          modalOptions: { windowClass: 'xl' },
        },
        {
          templateId: 'mediagroupaddmarkermodal',
        },
        null // <--- payload
      )
      .then((result: any) => {
        this.addAdSpace(result);
      })
      .catch(() => {});
  }

  addAdSpaceSelection(marker: MarkerFullDTO) {
    this.selectedMarkers = this.selectedMarkers.filter(e => e.id !== marker.id);
    this.selectedMarkers.push(marker);
  }

  removeAdSpaceSelection(marker: MarkerFullDTO) {
    this.selectedMarkers = this.selectedMarkers.filter(e => e.id !== marker.id);
  }

  addProductToMediumGroup(mediumGroup: MediumGroupFullDTO) {
    if (!mediumGroup.id) {
      throw new Error('missing id for mediumGroup');
    }
    this.modalService
      .showNavigationModal({
        entryComponent: {
          value: ProductsComponent,
          data: {
            selectedProducts: mediumGroup?.promotedProducts ?? [],
            modalType: 'productSelect',
          },
        },
        modalClasses: { body: 'tw-min-h-[532px]' }, // table-wrapper min-h 500px with padding results in 532px min-h
        title: this.translate.instant('lang.globals.assignProduct'),
        modalOptions: { windowClass: 'bigXL' },
      })
      .then((result: ProductDTO[]) => {
        if (result) {
          this.api
            .mm_MediumGroup_manageProducts(mediumGroup.id, result)
            .pipe(take(1))
            .subscribe({
              next: () => {},
              error: error => {
                this.errorHandler.errorHandler(error);
              },
            });
        }
      })
      .catch(() => {});
  }

  updateMissingResolutions() {
    this.missingResolution = [];
    this.missingMarker = [];
    Object.assign(this.missingResolution, this.setOfMarkerResolutions);
    for (const medium of this.mediumGroup.media) {
      const index = this.getArrayIndexByResolutionId(this.missingResolution, medium.resolution);
      if (index >= 0) {
        this.missingResolution.splice(index, 1);
      }
    }
    for (const resolution of this.missingResolution) {
      const index = this.getMarkerArrayIndexByResolutionId(this.inventoryMarker, resolution);
      if (index >= 0) {
        this.missingMarker.push(this.inventoryMarker[index]);
      }
    }
  }

  getArrayIndexByResolutionId(markerArray: ResolutionDTO[], resolution: ResolutionDTO): number {
    let index = -1;

    if (markerArray && resolution) {
      for (const ele of markerArray) {
        if (ele.id === resolution.id) {
          index = markerArray.indexOf(ele);
          break;
        }
      }
    }
    return index;
  }

  getMarkerArrayIndexByResolutionId(markerArray: InventoryMarkerFullDTO[], resolution: ResolutionDTO): number {
    let index = -1;

    if (markerArray && resolution) {
      for (const ele of markerArray) {
        if (ele.resolution.id === resolution.id) {
          index = markerArray.indexOf(ele);
          break;
        }
      }
    }
    return index;
  }

  createResolutionArrays(allMarker: InventoryMarkerFullDTO[]) {
    return new Promise(resolve => {
      const resolutionMap = new Map(this.setOfMarkerResolutions.map(res => [res.id, res]));

      for (const marker of allMarker) {
        if (!resolutionMap.has(marker.resolution.id)) {
          resolutionMap.set(marker.resolution.id, marker.resolution);
        }
      }

      this.setOfMarkerResolutions = Array.from(resolutionMap.values());
      resolve(true);
    });
  }

  saveAttributes() {
    this.api.ca_md_MediumGroupAttribute_editAttributes(this.mediumGroup.attributes).subscribe({
      next: attributes => {
        this.mediumGroup.attributes = attributes;
        this.attributesEdit = !this.attributesEdit;
      },
      error: err => {
        this.errorHandler.errorHandler(err);
      },
    });
  }

  durationCheck(medium: GroupMediumDTO) {
    if (!medium) {
      return null;
    }
    if (!medium?.duration) {
      return null;
    }
    if (medium.duration > this.mostOccurredDuration + 0.5 || medium.duration < this.mostOccurredDuration - 0.5) {
      return true;
    }
    return null;
  }

  private updateDuplicateResolutions() {
    this.duplicateResolutions = [];
    const resolutionMap = new Map<string, ResolutionDTO>();

    for (const medium of this.mediumGroup.media) {
      const resolution = medium.resolution;
      if (resolutionMap.has(resolution.id)) {
        this.duplicateResolutions.push(resolution);
      } else {
        resolutionMap.set(resolution.id, resolution);
      }
    }

    this.hasDuplicateResolution = this.duplicateResolutions.length > 0;
  }

  duplicateCheck(medium: GroupMediumDTO) {
    if (!medium) {
      return null;
    }
    if (!medium?.resolution) {
      return null;
    }
    return this.duplicateResolutions.some(resolution => resolution.id === medium.resolution.id);
  }

  deleteCategory(): void {
    this.mediumGroup.category = null;
    this.editMediumGroup();
  }

  createCategory(): void {
    const newCategory: MediumGroupCategoryDTO = new MediumGroupCategoryDTO();
    this.modalService
      .showNavigationModal({
        entryComponent: { value: MediumGroupCategoryEditComponent, data: { mediumGroupCategory: newCategory } },
        title: null,
        modalOptions: { windowClass: 'M' },
      })
      .then((result: MediumGroupCategoryDTO) => {
        if (result) {
          this.api.mm_MediumGroupCategory_createCategory(result).subscribe({
            next: category => {
              this.mediumGroup.category = category;
              this.editMediumGroup();
            },
          });
        } else {
          this.mediumGroup.category = null;
          this.editMediumGroup();
        }
      })
      .catch(() => {});
  }

  selectCategory(): void {
    this.modalService
      .showNavigationModal({
        entryComponent: { value: MediumGroupCategoryListComponent, data: {} },
        title: null,
        modalOptions: { windowClass: 'M' },
      })
      .then((result: MediumGroupCategoryDTO) => {
        if (result) {
          this.mediumGroup.category = result;
        } else {
          this.mediumGroup.category = null;
        }
        this.editMediumGroup();
      })
      .catch(() => {});
  }

  showMedium(medium: GroupMediumDTO) {
    this.navService.navigateToPoint(this.util.RoutingID.mediumExplorer, null, { queryParams: { mediumID: medium.id } }).then();
  }

  updateAttribute(attribute: MediumGroupAttributeDTO, $event: string[]) {
    attribute.value = $event.join(', ');
  }

  getAttributeInputData(attribute: MediumGroupAttributeDTO) {
    if (!attribute.value) {
      return [];
    }
    return attribute.value.split(',').map(value => value.trim());
  }
}
